import { useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LaunchIcon from "@mui/icons-material/Launch";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { format } from "date-fns";

import { useAppDispatch } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import { openForm } from "features/scheduling/scheduler-slice";
import useUserRoles from "hooks/use-user-roles.hook";

export default function InfoBlock() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setupAppointmentModal, appointmentFormId } = useAppointments();
  const { userPermissions, currentUser: staffMember } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const {
    activeCallDetails,
    isPastAppointment,
    appointment,
    patient,
    isLoading
  } = useSunoDialer();
  const { callerId } = activeCallDetails || {};

  return (
    <Box sx={{ px: 2, pt: 2, pb: 0 }}>
      <Stack
        spacing={1.5}
        sx={{
          color: "white",
          background: "hsla(273, 21%, 54%, 0.85)",
          px: 2,
          pt: 1,
          pb: 1,
          borderRadius: "8px",
          boxShadow: theme.shadows[3]
        }}
      >
        {isLoading && <CircularProgress sx={{ p: 0 }} />}
        {!isLoading && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            {Boolean(callerId && appointment?.startDate) ? (
              <Stack spacing={0.5} paddingRight={2} width="100%">
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    pb: 0.25
                  }}
                >
                  {isPastAppointment ? "Past" : "Next"} Appointment:
                </Typography>
                <Link
                  href={`/patients/${callerId}/appointments/${
                    appointment?.id || ""
                  }`}
                  sx={{
                    color: "white",
                    textDecorationColor: grey[500],
                    "&:hover": {
                      color: "white"
                    }
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    if (callerId) {
                      navigate(
                        `/patients/${callerId}/appointments/${
                          appointment?.id || ""
                        }`
                      );
                    }
                  }}
                >
                  <Typography sx={{ fontSize: "15px", lineHeight: 1.3 }}>
                    <CalendarMonthIcon
                      sx={{ fontSize: "15px", transform: "translateY(1px)" }}
                    />{" "}
                    {format(
                      new Date(appointment?.startDate || ""),
                      "EEE MMM dd, yyyy hh:mm aaa"
                    )}
                    <IconButton
                      size="small"
                      sx={{
                        ml: -0.5,
                        mt: -0.8,
                        transform: "scale(0.6)",
                        opacity: 0.7,
                        "&:hover": {
                          color: misc.purpleText,
                          opacity: 1
                        }
                      }}
                    >
                      <LaunchIcon sx={{ color: "white" }} />
                    </IconButton>
                  </Typography>
                </Link>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  pb: 0.25
                }}
              >
                No Appointments found
              </Typography>
            )}

            <Stack
              spacing={1}
              width="80px"
              sx={{
                cursor: "pointer",
                alignItems: "center",
                "&:hover": {
                  color: neutral[300]
                }
              }}
              onClick={() => {
                setupAppointmentModal(
                  {
                    patient,
                    staffMember: viewOwnScheduleOnly ? staffMember : undefined,
                    staffMemberId: viewOwnScheduleOnly
                      ? staffMember?.id
                      : undefined
                  },
                  "add"
                );
                dispatch(openForm(appointmentFormId));
              }}
            >
              <IconButton
                size="small"
                sx={{
                  border: `solid 1px white`,
                  color: "white",
                  width: "30px",
                  height: "30px",
                  "&:hover": {
                    color: neutral[300],
                    border: `solid 1px ${neutral[300]}`
                  }
                }}
              >
                <AddIcon />
              </IconButton>

              <Typography
                sx={{
                  fontSize: "12px",
                  lineHeight: 1.2,
                  textAlign: "center"
                }}
              >
                New Appointment
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
